<template>
  <v-container fluid class="pa-0">
    <v-card outlined class="pa-4 mb-4">
      <v-card-title class="pa-0 text-h6">
        {{ isEdit ? 'Редактирование счёта' : 'Создание счёта' }}
      </v-card-title>
    </v-card>

    <v-form ref="form" lazy-validation @submit.prevent>
      <v-card v-if="!isEdit" outlined class="pa-4 mb-4">
        <v-card-title class="pa-0 text-subtitle-1 font-weight-medium">
          Автозаполнение
        </v-card-title>
        <v-divider class="my-3" />
        <v-autocomplete
          v-model="templateInvoiceId"
          :items="templateOptions"
          :loading="templatesLoading"
          item-text="label"
          item-value="id"
          label="Автозаполнение из ранее выставленного счета"
          filled
          dense
          clearable
          :no-data-text="templatesLoading ? 'Загрузка…' : 'Нет ранее заполненных счетов'"
          hint="Заполнит проект, договор, приложение, реквизиты и позиции. Дата, период и комментарий останутся пустыми."
          persistent-hint
          @change="onTemplateSelected"
        />
      </v-card>

      <v-card outlined class="pa-4 mb-4">
        <v-card-title class="pa-0 text-subtitle-1 font-weight-medium">
          Договор
        </v-card-title>
        <v-divider class="my-3" />
        <v-row>
          <v-col cols="12" md="6">
            <ProjectSelector
              v-model="form.project_id"
              single
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="form.onec_contract_key"
              :items="oneCContracts"
              :loading="oneCContractsLoading || contractLoading"
              item-text="name"
              item-value="ref_key"
              label="Договор *"
              filled
              dense
              clearable
              :rules="[requiredRule]"
              :no-data-text="oneCContractsLoading ? 'Загрузка…' : 'В 1С нет договоров'"
            />
            <div class="caption grey--text mt-n2">
              Список из 1С. Реквизиты заполнятся автоматически
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.appendix_id"
              :items="appendices"
              item-text="label"
              item-value="id"
              label="Приложение"
              filled
              dense
              clearable
              :disabled="!hasProjectSelected"
              :loading="appendicesLoading"
              :hint="appendixHint"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.invoice_date"
              label="Дата счёта *"
              type="date"
              filled
              dense
              :rules="[requiredRule]"
            />
          </v-col>
        </v-row>
      </v-card>

      <v-card outlined class="pa-4 mb-4">
        <v-card-title class="pa-0 text-subtitle-1 font-weight-medium">
          Реквизиты счета
        </v-card-title>
        <v-divider class="my-3" />
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="requisites.counterparty"
              label="Контрагент"
              filled
              dense
              readonly
              :loading="contractLoading"
              :rules="[requiredRule]"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="requisites.inn"
              label="ИНН"
              filled
              dense
              readonly
              :loading="contractLoading"
              :rules="[requiredRule]"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="requisites.kpp"
              label="КПП"
              filled
              dense
              readonly
              :loading="contractLoading"
            />
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="requisites.legal_address"
              label="Юридический адрес"
              filled
              dense
              readonly
              rows="2"
              auto-grow
              :loading="contractLoading"
            />
          </v-col>
        </v-row>
      </v-card>

      <v-card outlined class="pa-4 mb-4">
        <div class="d-flex align-center justify-space-between mb-2">
          <v-card-title class="pa-0 text-subtitle-1 font-weight-medium">
            Пакеты услуг и дополнительные начисления
          </v-card-title>
          <v-btn
            color="primary"
            small
            text
            @click="addPackageRow"
          >
            <v-icon left small>mdi-plus</v-icon>
            Добавить
          </v-btn>
        </div>
        <v-divider class="mb-3" />

        <div
          v-for="(pkg, index) in form.packages"
          :key="`package-${index}`"
          class="mb-4 pa-3"
          style="border: 1px solid #e0e0e0; border-radius: 4px;"
        >
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="subtitle-2">Позиция {{ index + 1 }}</div>
            <v-btn
              icon
              small
              color="error"
              :disabled="form.packages.length <= 1"
              @click="removePackageRow(index)"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="pkg.invoice_name"
                label="Название в счёте *"
                hint='Пример: Пакет услуг "Мультисервис"'
                persistent-hint
                filled
                dense
                :rules="[requiredRule]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="pkg.onec_nomenclature_key"
                :items="nomenclatureOptions"
                :loading="oneCNomenclatureLoading"
                item-text="name"
                item-value="ref_key"
                label="Номенклатура в счёте *"
                persistent-hint
                filled
                dense
                clearable
                :rules="[requiredRule]"
                :no-data-text="oneCNomenclatureLoading ? 'Загрузка…' : 'В 1С нет номенклатуры'"
                @change="onNomenclatureSelected(pkg)"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-menu
                v-model="pkg.periodMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    :value="formatPeriodMonth(pkg.period)"
                    label="Период *"
                    filled
                    dense
                    readonly
                    clearable
                    :rules="[requiredRule]"
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="pkg.period = null"
                  />
                </template>
                <v-date-picker
                  v-model="pkg.period"
                  type="month"
                  locale="ru-ru"
                  active-picker="YEAR"
                  min="2020-01"
                  :max="maxPeriodMonth"
                  @change="pkg.periodMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="pkg.price"
                label="Цена в счёте *"
                type="number"
                min="0"
                step="0.01"
                filled
                dense
                :rules="[requiredRule]"
              />
            </v-col>
          </v-row>
        </div>

        <div class="caption grey--text" v-if="!form.packages.length">
          Добавьте хотя бы одну позицию.
        </div>
        <div class="mt-2 font-weight-medium">
          Итого: {{ formatAmount(totalAmount) }}
        </div>
      </v-card>

      <v-card outlined class="pa-4 mb-4">
        <v-card-title class="pa-0 text-subtitle-1 font-weight-medium">
          Комментарий
        </v-card-title>
        <v-divider class="my-3" />
        <v-text-field
          v-model="form.comment"
          label="Комментарий"
          filled
          dense
        />
      </v-card>

      <v-row>
        <v-col cols="12" class="d-flex" style="gap: 8px">
          <v-btn
            v-if="!sentToOneC"
            outlined
            color="primary"
            :disabled="loading"
            :loading="saving === 'draft'"
            @click="onSave(false)"
          >
            Сохранить черновик
          </v-btn>
          <v-btn
            color="primary"
            :disabled="loading"
            :loading="saving === 'send'"
            @click="onSave(true)"
          >
            Сохранить и отправить
          </v-btn>
          <v-btn text :disabled="loading" @click="goBack">Отмена</v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import ProjectSelector from '~/components/partials/selectors/ProjectSelector'

const emptyPackage = () => ({
  invoice_name: '',
  gate_name: '',
  onec_nomenclature_key: null,
  onec_nomenclature_name: '',
  operator_package_id: null,
  additional_service_id: null,
  period: null,
  periodMenu: false,
  price: 0,
})

const monthToPeriodRange = (month) => {
  if (!month) {
    return { period_from: null, period_to: null }
  }
  const start = moment(month, 'YYYY-MM').startOf('month')
  return {
    period_from: start.format('YYYY-MM-DD'),
    period_to: start.clone().endOf('month').format('YYYY-MM-DD'),
  }
}

export default {
  name: 'DocumentFlowInvoiceForm',
  components: { ProjectSelector },

  props: {
    invoiceId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      form: {
        project_id: [],
        onec_contract_key: null,
        appendix_id: null,
        invoice_date: new Date().toISOString().slice(0, 10),
        comment: '',
        packages: [emptyPackage()],
      },
      requisites: {
        counterparty: '',
        inn: '',
        kpp: '',
        legal_address: '',
      },
      appendices: [],
      oneCContracts: [],
      oneCContractsLoading: false,
      oneCNomenclature: [],
      oneCNomenclatureLoading: false,
      appendicesLoading: false,
      contractLoading: false,
      projectLoadToken: 0,
      contractLoadToken: 0,
      lastLoadedProjectId: null,
      lastLoadedContractKey: null,
      loading: false,
      saving: null,
      sentToOneC: false,
      loadingInvoice: false,
      templatesLoading: false,
      templateInvoiceId: null,
      invoiceTemplates: [],
      requiredRule: (v) => !!v || v === 0 || 'Обязательное поле',
    }
  },

  computed: {
    isEdit() {
      return !!this.invoiceId
    },
    templateOptions() {
      return this.invoiceTemplates.map((invoice) => ({
        id: invoice.id,
        label: this.formatTemplateLabel(invoice),
      }))
    },
    maxPeriodMonth() {
      return moment().add(3, 'months').format('YYYY-MM')
    },
    nomenclatureOptions() {
      const options = [...this.oneCNomenclature]
      const known = new Set(options.map((item) => item.ref_key))

      this.form.packages.forEach((pkg) => {
        if (pkg.onec_nomenclature_key && !known.has(pkg.onec_nomenclature_key)) {
          known.add(pkg.onec_nomenclature_key)
          options.push({
            ref_key: pkg.onec_nomenclature_key,
            name: pkg.onec_nomenclature_name || pkg.onec_nomenclature_key,
          })
        }
      })

      return options
    },
    totalAmount() {
      return this.form.packages.reduce((sum, pkg) => sum + Number(pkg.price || 0), 0)
    },
    hasProjectSelected() {
      return !!this.normalizeSelectorValue(this.form.project_id)
    },
    appendixHint() {
      if (!this.hasProjectSelected) {
        return 'Сначала выберите проект'
      }
      if (this.appendicesLoading) {
        return 'Загрузка приложений…'
      }
      if (!this.appendices.length) {
        return 'У проекта нет приложений'
      }
      return ''
    },
  },

  watch: {
    invoiceId: {
      immediate: true,
      handler(value) {
        if (value) {
          this.loadInvoice()
        }
      },
    },
    'form.project_id': {
      deep: true,
      handler() {
        if (this.loadingInvoice) {
          return
        }

        const projectId = this.normalizeSelectorValue(this.form.project_id)
        if (String(projectId ?? '') === String(this.lastLoadedProjectId ?? '')) {
          return
        }

        this.lastLoadedProjectId = projectId
        this.onProjectChange(projectId)
      },
    },
    'form.onec_contract_key'(value) {
      if (this.loadingInvoice) {
        return
      }
      if (value === this.lastLoadedContractKey) {
        return
      }

      this.lastLoadedContractKey = value
      this.onOneCContractChange(value)
    },
  },

  mounted() {
    this.loadOneCContracts()
    this.loadOneCNomenclature()
    if (!this.isEdit) {
      this.loadTemplates()
    }
  },

  methods: {
    ...mapActions('document_flow/invoices', [
      'fetchInvoice',
      'createInvoice',
      'updateInvoice',
      'fetchOneCContracts',
      'fetchOneCContract',
      'fetchOneCNomenclature',
      'fetchProjectAppendices',
      'fetchInvoiceTemplates',
    ]),

    normalizeSelectorValue(value) {
      if (Array.isArray(value)) {
        return value[0] ?? null
      }
      return value ?? null
    },

    unwrapList(value) {
      if (!value) {
        return []
      }
      if (Array.isArray(value)) {
        return value
      }
      if (Array.isArray(value.data)) {
        return value.data
      }
      return []
    },

    normalizeAppendices(appendices) {
      return this.unwrapList(appendices).map((appendix) => ({
        ...appendix,
        operator_packages: this.unwrapList(appendix.operator_packages),
        additional_services: this.unwrapList(appendix.additional_services),
      }))
    },

    formatAmount(value) {
      return Number(value || 0).toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    formatPeriodMonth(month) {
      return month ? moment(month, 'YYYY-MM').locale('ru').format('MMMM YYYY') : ''
    },

    formatTemplateLabel(invoice) {
      const number = invoice.number || `#${invoice.id}`
      const date = invoice.date
        ? moment(String(invoice.date).slice(0, 10)).format('DD.MM.YYYY')
        : 'без даты'
      const project = invoice.project_name || 'без заказчика'
      const contract = invoice.contract_number || 'без договора'
      return `№ ${number} от ${date} · ${project} · ${contract}`
    },

    async loadTemplates() {
      this.templatesLoading = true
      try {
        this.invoiceTemplates = await this.fetchInvoiceTemplates()
      } catch (error) {
        this.invoiceTemplates = []
        console.error(error)
      } finally {
        this.templatesLoading = false
      }
    },

    async onTemplateSelected(templateId) {
      if (!templateId) {
        return
      }

      const invoice = this.invoiceTemplates.find((item) => Number(item.id) === Number(templateId))
      if (!invoice) {
        return
      }

      this.loadingInvoice = true
      try {
        this.form.project_id = invoice.project_id ? [invoice.project_id] : []
        this.form.onec_contract_key = invoice.onec_contract_key || null
        this.lastLoadedProjectId = invoice.project_id || null
        this.lastLoadedContractKey = invoice.onec_contract_key || null
        this.form.appendix_id = invoice.appendix_id || null
        this.form.invoice_date = ''
        this.form.comment = ''
        this.form.packages = (invoice.packages && invoice.packages.length)
          ? invoice.packages.map((item) => ({
            invoice_name: item.invoice_name || '',
            gate_name: item.gate_name || '',
            onec_nomenclature_key: item.onec_nomenclature_key || null,
            onec_nomenclature_name: item.onec_nomenclature_name || '',
            operator_package_id: item.operator_package_id,
            additional_service_id: item.additional_service_id,
            period: null,
            periodMenu: false,
            price: Number(item.price || 0),
          }))
          : [emptyPackage()]

        this.requisites = {
          counterparty: invoice.counterparty_name || '',
          inn: invoice.counterparty_inn || '',
          kpp: invoice.counterparty_kpp || '',
          legal_address: invoice.legal_address || '',
        }

        if (invoice.project_id) {
          await this.loadProjectAppendices(invoice.project_id, { keepAppendix: true })
        } else {
          this.appendices = []
        }

        if (invoice.onec_contract_key) {
          await this.loadOneCContractDetails(invoice.onec_contract_key, {
            keepRequisites: true,
          })
        }
      } finally {
        this.loadingInvoice = false
      }
    },

    addPackageRow() {
      this.form.packages.push(emptyPackage())
    },

    removePackageRow(index) {
      if (this.form.packages.length <= 1) {
        return
      }
      this.form.packages.splice(index, 1)
    },

    onNomenclatureSelected(pkg) {
      const selected = this.nomenclatureOptions.find((item) => item.ref_key === pkg.onec_nomenclature_key)
      pkg.onec_nomenclature_name = selected ? selected.name : ''
      if (selected && !pkg.invoice_name) {
        pkg.invoice_name = selected.name
      }
    },

    async loadOneCNomenclature() {
      this.oneCNomenclatureLoading = true
      try {
        this.oneCNomenclature = await this.fetchOneCNomenclature()
      } catch (error) {
        this.oneCNomenclature = []
        console.error(error)
      } finally {
        this.oneCNomenclatureLoading = false
      }
    },

    async loadInvoice() {
      this.loadingInvoice = true

      try {
        const invoice = await this.fetchInvoice(this.invoiceId)
        this.sentToOneC = !invoice.is_draft
        const packages = (invoice.packages && invoice.packages.length)
          ? invoice.packages.map((item) => ({
            invoice_name: item.invoice_name || '',
            gate_name: item.gate_name || '',
            onec_nomenclature_key: item.onec_nomenclature_key || null,
            onec_nomenclature_name: item.onec_nomenclature_name || '',
            operator_package_id: item.operator_package_id,
            additional_service_id: item.additional_service_id,
            period: item.period_from ? String(item.period_from).slice(0, 7) : null,
            periodMenu: false,
            price: Number(item.price || 0),
          }))
          : [emptyPackage()]

        this.form = {
          project_id: invoice.project_id ? [invoice.project_id] : [],
          onec_contract_key: invoice.onec_contract_key || null,
          appendix_id: invoice.appendix_id,
          invoice_date: invoice.date ? String(invoice.date).slice(0, 10) : '',
          comment: invoice.comment || '',
          packages,
        }
        this.requisites = {
          counterparty: invoice.counterparty_name || '',
          inn: invoice.counterparty_inn || '',
          kpp: invoice.counterparty_kpp || '',
          legal_address: invoice.legal_address || '',
        }
        this.lastLoadedProjectId = invoice.project_id || null
        this.lastLoadedContractKey = invoice.onec_contract_key || null

        if (invoice.project_id) {
          await this.loadProjectAppendices(invoice.project_id, { keepAppendix: true })
        } else {
          this.appendices = []
        }

        if (invoice.onec_contract_key) {
          await this.loadOneCContractDetails(invoice.onec_contract_key, {
            keepRequisites: true,
          })
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.loadingInvoice = false
      }
    },

    async loadOneCContracts() {
      this.oneCContractsLoading = true
      try {
        this.oneCContracts = await this.fetchOneCContracts()
        this.ensureContractOption()
      } catch (error) {
        this.oneCContracts = []
        console.error(error)
      } finally {
        this.oneCContractsLoading = false
      }
    },

    ensureContractOption(contract = null) {
      const refKey = contract?.ref_key || this.form.onec_contract_key
      if (!refKey) {
        return
      }
      if (this.oneCContracts.some((item) => item.ref_key === refKey)) {
        return
      }
      this.oneCContracts = [
        ...this.oneCContracts,
        {
          ref_key: refKey,
          name: contract?.name || contract?.number || refKey,
          number: contract?.number || '',
        },
      ]
    },

    async onProjectChange(projectId) {
      this.projectLoadToken = (this.projectLoadToken || 0) + 1
      const loadToken = this.projectLoadToken
      this.form.appendix_id = null
      this.appendices = []

      if (!projectId) {
        return
      }

      await this.loadProjectAppendices(projectId, {
        keepAppendix: false,
        loadToken,
      })
    },

    async onOneCContractChange(refKey) {
      this.contractLoadToken = (this.contractLoadToken || 0) + 1
      const loadToken = this.contractLoadToken

      if (!refKey) {
        this.applyRequisitesFromContract({})
        return
      }

      await this.loadOneCContractDetails(refKey, {
        keepRequisites: false,
        loadToken,
      })
    },

    applyRequisitesFromContract(contract) {
      this.requisites = {
        counterparty: contract.counterparty || '',
        inn: contract.inn || '',
        kpp: contract.kpp || '',
        legal_address: contract.legal_address || '',
      }
    },

    async loadProjectAppendices(projectId, options = {}) {
      const { keepAppendix = false, loadToken = null } = options

      this.appendicesLoading = true
      try {
        const appendices = await this.fetchProjectAppendices(projectId)

        if (loadToken !== null && loadToken !== this.projectLoadToken) {
          return
        }

        this.appendices = this.normalizeAppendices(appendices).map((appendix) => ({
          ...appendix,
          label: appendix.label || this.formatAppendixLabel(appendix),
        }))

        const appendixStillExists = this.appendices.some(
          (item) => Number(item.id) === Number(this.form.appendix_id),
        )
        if (!keepAppendix || (this.form.appendix_id && !appendixStillExists)) {
          this.form.appendix_id = null
        }
      } catch (error) {
        console.error(error)
        if (loadToken === null || loadToken === this.projectLoadToken) {
          this.appendices = []
        }
      } finally {
        if (loadToken === null || loadToken === this.projectLoadToken) {
          this.appendicesLoading = false
        }
      }
    },

    formatAppendixLabel(appendix) {
      const name = appendix.number || ''
      const contract = appendix.contract_number || ''
      return contract ? `${name} (${contract})` : name
    },

    async loadOneCContractDetails(refKey, options = {}) {
      const { keepRequisites = false, loadToken = null } = options

      this.contractLoading = true
      try {
        const contract = await this.fetchOneCContract(refKey)

        if (loadToken !== null && loadToken !== this.contractLoadToken) {
          return
        }

        this.ensureContractOption(contract)

        if (!keepRequisites) {
          this.applyRequisitesFromContract(contract)
        }
      } catch (error) {
        console.error(error)
      } finally {
        if (loadToken === null || loadToken === this.contractLoadToken) {
          this.contractLoading = false
        }
      }
    },

    buildPayload(send) {
      return {
        send,
        project_id: this.normalizeSelectorValue(this.form.project_id),
        onec_contract_key: this.form.onec_contract_key,
        appendix_id: this.form.appendix_id,
        invoice_date: this.form.invoice_date,
        comment: this.form.comment,
        counterparty: this.requisites.counterparty || null,
        inn: this.requisites.inn || null,
        kpp: this.requisites.kpp || null,
        legal_address: this.requisites.legal_address || null,
        packages: this.form.packages.map((pkg) => ({
          invoice_name: pkg.invoice_name,
          gate_name: pkg.gate_name || null,
          onec_nomenclature_key: pkg.onec_nomenclature_key || null,
          onec_nomenclature_name: pkg.onec_nomenclature_name || null,
          operator_package_id: pkg.operator_package_id,
          additional_service_id: pkg.additional_service_id,
          ...monthToPeriodRange(pkg.period),
          price: Number(pkg.price || 0),
        })),
      }
    },

    async onSave(send) {
      if (!this.$refs.form.validate()) {
        return
      }

      if (!this.form.packages.length) {
        return
      }

      if (!this.requisites.counterparty || !this.requisites.inn) {
        this.$store.commit(
          'shared/SET_ERROR',
          { message: 'Заполните контрагента и ИНН' },
          { root: true },
        )
        return
      }

      const payload = this.buildPayload(send)

      this.loading = true
      this.saving = send ? 'send' : 'draft'
      try {
        if (this.isEdit) {
          await this.updateInvoice({ id: this.invoiceId, data: payload })
        } else {
          await this.createInvoice(payload)
        }
        this.$store.commit(
          'shared/SET_SUCCESS',
          { message: send ? 'Счёт отправлен в 1С и на почту' : 'Черновик сохранён' },
          { root: true },
        )
        this.goBack()
      } catch (error) {
        const mailError = error?.response?.data?.errors?.mail?.[0]
        const validationError = mailError
          || Object.values(error?.response?.data?.errors || {}).flat()[0]
        if (mailError) {
          this.$store.commit('shared/SET_ERROR', { message: mailError }, { root: true })
          this.goBack()
          return
        }
        this.$store.commit(
          'shared/SET_ERROR',
          { message: validationError || (send ? 'Не удалось отправить счёт' : 'Не удалось сохранить черновик') },
          { root: true },
        )
        console.error(error)
      } finally {
        this.loading = false
        this.saving = null
      }
    },

    goBack() {
      this.$router.push({ name: 'document-flow.invoices.list' })
    },
  },
}
</script>
